import { EnforceTitle } from "@/components/enforce-title";
import { DisabledFormProvider } from "@/flokit/compound/inputs/contexts/disabled-form-context";
import { DebugModeContainer } from "@/flokit/core/debug/container";
import { TooltipProvider } from "@/flokit/core/tooltip/tooltip";
import { GlobalIsDirtyContextProvider } from "@/flokit/utils/global-is-dirty/global-is-dirty-context";
import NiceModal from "@ebay/nice-modal-react";
import { withLDProvider } from "launchdarkly-react-client-sdk";
import { AnalyticsProvider } from "modules/analytics/analytics";
import { NextQueryParamProvider } from "next-query-params";
import getConfig from "next/config";
import Head from "next/head";
import React from "react";
import { Toaster } from "react-hot-toast";
import { QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import ConditionallyRender from "../components/conditionally-render";
import IOSWindowFix from "../components/ios-window-fix";
import { ModalManager } from "../components/services/modal.service";
import { PanelManager } from "../components/services/panel.service";
import AdminLoginGuard from "../modules/admin-login-guard";
import { LaunchDarklyClientIdentifier } from "../modules/analytics/launch-darkly-client-identifier";
import { AppQueryClient } from "../utils/QueryClient";
import "./tw.css";

if (process.browser) {
  window.addEventListener("error", function (event) {
    event.preventDefault();
    event.stopPropagation();
  });
}

const MyApp: React.FC<any> = ({ Component, pageProps }) => {
  return (
    <NextQueryParamProvider>
      <Toaster
        containerStyle={{
          bottom: 84,
          padding: 0,
          margin: 0,
        }}
        containerClassName="!p-0 !m-0"
        toastOptions={{
          className: "!p-0 !m-0 !bg-transparent !shadow-none",
          duration: 50000,
        }}
      />
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        <script
          src="https://cdnjs.cloudflare.com/ajax/libs/lokijs/1.5.12/lokijs.min.js"
          integrity="sha512-aSbdfyDpBetjOHWSkZDhdg+wTgQvG/6inTOzFVHJ/0Z6h3Bx2vLGVzp+/902uDdr8S9f4GultmcefJh9/Ymi0Q=="
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
        />
      </Head>
      <TooltipProvider>
        <DisabledFormProvider disabled={false}>
          <ToastContainer theme={"colored"} />
          <DebugModeContainer />
          <QueryClientProvider client={AppQueryClient}>
            <AnalyticsProvider />
            <LaunchDarklyClientIdentifier>
              <ConditionallyRender client>
                <IOSWindowFix />
              </ConditionallyRender>
              <PanelManager />
              <ModalManager />
              <AdminLoginGuard>
                <EnforceTitle>
                  <GlobalIsDirtyContextProvider>
                    <NiceModal.Provider>
                      <Component {...pageProps} />
                    </NiceModal.Provider>
                  </GlobalIsDirtyContextProvider>
                </EnforceTitle>
              </AdminLoginGuard>
              <ReactQueryDevtools initialIsOpen={false} />
            </LaunchDarklyClientIdentifier>
          </QueryClientProvider>
        </DisabledFormProvider>
      </TooltipProvider>
    </NextQueryParamProvider>
  );
};

const getNodeEnv = () => {
  const { publicRuntimeConfig } = getConfig();

  return {
    LD_CLIENT_ID: publicRuntimeConfig.LD_CLIENT_ID,
  };
};

export default withLDProvider({
  clientSideID: getNodeEnv()?.LD_CLIENT_ID,
  context: {
    key: "flo_nextJS",
  },
})(MyApp);
